import { FilterField } from 'src/app/components/dialogs/select-filter-new/models/filter-field';
import { FilterFieldGroup } from 'src/app/components/dialogs/select-filter-new/models/filter-field-group';
import { FieldDisplayType } from 'src/app/components/dialogs/select-filter-new/models/field-display-type';
import { DynamicChoiceRepository } from './dynamic-choice-repository';
import { FieldValue } from '../models/field-value';
import { FilterMatch, FilterType, SavedFilter } from '../models/saved-filter';
import { SavedFilterFieldFactory } from '../models/saved-filter-field';

export class SelectionManager {
    selectedFields: FieldValue[] = [];
    groups: FilterFieldGroup[] = [];
    private selectedReportUniqueFilterHits: Set<string> = new Set<string>();

    constructor(private choiceRepo: DynamicChoiceRepository) { }

    setFields(groups: FilterFieldGroup[]) {
        this.groups = groups;
    }

    optionSelected(option: any) {
        if (option.fields) option.fields.forEach(f => this.addField(f)); // An entire group was selected
        else this.addField(option); // A single field was selected
    }

    private addField(option: FilterField) {
        // If the filter field has already been added, do nothing (we don't allow duplicate fields)
        if (this.selectedFields.find(f => f.field.fieldId === option.fieldId)) return;

        // Dynamic drop-downs and multi-selects need extra data - go get it
        if (option.displayType === FieldDisplayType.MultiSelect) this.choiceRepo.loadChoicesFor(option);

        this.selectedFields.push(new FieldValue(option));
    }

    removeField(fieldIndex: number) {
        const removedField = this.selectedFields.splice(fieldIndex, 1);
        if (removedField[0]?.field && this.selectedReportUniqueFilterHits.has(removedField[0].field.fieldId))
            this.selectedReportUniqueFilterHits.delete(removedField[0].field.fieldId);
    }

    serialize(): SavedFilter {
        const savedFilter: SavedFilter = {
            type: FilterType.Simple,
            match: FilterMatch.All,
            not: false,
            fields: []
        };
        // Remove fields with no value (they were added to the filter but no value was entered)
        for (let index = 0; index < this.selectedFields.length;) {
            // booleans (checkboxes) should be kept even if they're false (so the user can filter on the false case)
            // text should be kept even if empty (so the user can search for an empty field)
            if (this.selectedFields[index].field.displayType === FieldDisplayType.Boolean ||
                this.selectedFields[index].field.displayType === FieldDisplayType.CustomCodingCheckbox || 
                this.selectedFields[index].field.displayType === FieldDisplayType.CustomCodingSharedCheckbox ||
                this.selectedFields[index].field.displayType === FieldDisplayType.Text) {
                index++;
                continue;
            }

            // add the unique filter option if present.
            if (this.selectedFields[index].field.displayType === FieldDisplayType.SearchReport && this.selectedFields[index].value.length > 0) {
                this.selectedFields[index].value.unshift(this.selectedReportUniqueFilterHits.has(this.selectedFields[index].field.fieldId)); 
            }

            // value.length === 0 indicates no value was entered
            // !value[0] indicates undefined, null, an empty string, or false - none of which should be serialized
            if (this.selectedFields[index].value.length === 0 || this.selectedFields[index].value.every(v => !v)) this.selectedFields.splice(index, 1);
            else index++;
        }

        this.selectedFields.forEach(sf => savedFilter.fields.push(SavedFilterFieldFactory.createFromSimple(sf)));

        return savedFilter;
    }

    public isFieldSelected(fieldId: string): boolean {
		return this.selectedReportUniqueFilterHits.has(fieldId);
	}

	public toggleFieldSelection(fieldId: string, isSelected: boolean): void {
		if (isSelected && !this.selectedReportUniqueFilterHits.has(fieldId)) {
			this.selectedReportUniqueFilterHits.add(fieldId);
		} else if (!isSelected && this.selectedReportUniqueFilterHits.has(fieldId)) {
			this.selectedReportUniqueFilterHits.delete(fieldId);
		}
	}

    public getSelectedUniqueReportIds(): Set<string> {
        const filterUniqueIds = new Set<string>;
        this.selectedFields.forEach((sf, idx) => {
            if (sf.field.displayType === FieldDisplayType.SearchReport && this.selectedReportUniqueFilterHits.has(sf.field.fieldId))
                filterUniqueIds.add(`${sf.field.fieldId}_${idx}`)
        });
        return filterUniqueIds;
    }

    public seedUniqueFilterHits(searchReportIds: Set<string>): void {
        searchReportIds.forEach(id => this.selectedReportUniqueFilterHits.add(id));
    }
}
